import React from "react";
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonButton,
  IonIcon,
} from "@ionic/react";
import { closeOutline, sparklesOutline } from "ionicons/icons";
import "./CategoryModal.css";

interface CategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (category: string, label: string) => void;
}

interface CategoryItem {
  value: string;
  label: string;
}

interface CategorySection {
  emoji: string;
  title: string;
  note: string;
  items: CategoryItem[];
}

const CATEGORY_SECTIONS: CategorySection[] = [
  {
    emoji: "💬",
    title: "App Feedback",
    note: "Help us improve the app experience and AI quality.",
    items: [
      { value: "report-bug", label: "Report a Bug" },
      { value: "suggest-feature", label: "Suggest an Improvement" },
      { value: "ai-feedback", label: "AI Chat Response Feedback" },
    ],
  },
  {
    emoji: "🏙",
    title: "Tourism & Activities",
    note: "Share discoveries, updates, and local visitor highlights.",
    items: [
      { value: "whats-new", label: "What’s New in Bacolod" },
      { value: "attractions", label: "Attractions & Landmarks" },
      { value: "events", label: "Events & Festivals" },
      { value: "masskara", label: "MassKara Festival Updates" },
      { value: "nature", label: "Nature, Parks & Outdoor Spots" },
      { value: "history-culture", label: "History, Culture & Heritage" },
      { value: "itineraries", label: "Suggested Itineraries" },
      { value: "local-tips", label: "Local Insider Tips" },
    ],
  },
  {
    emoji: "🍽",
    title: "Food & Lifestyle",
    note: "Recommend places, trends, and standout city picks.",
    items: [
      { value: "restaurants", label: "Restaurants & Local Eats" },
      { value: "cafes", label: "Cafes & Coffee Shops" },
      { value: "cakes-desserts", label: "Cakes, Desserts & Pastries" },
      { value: "nightlife", label: "Nightlife, Bars & Entertainment" },
      { value: "shopping", label: "Shops, Markets & Malls" },
      { value: "wellness", label: "Spas, Salons & Wellness" },
    ],
  },
  {
    emoji: "🚗",
    title: "Travel & Services",
    note: "Contribute route, stay, and visitor service information.",
    items: [
      { value: "accommodations", label: "Hotels, Stays & Inns" },
      { value: "tour-packages", label: "Tour Packages & Guides" },
      { value: "rent-car", label: "Car & Vehicle Rentals" },
      { value: "services", label: "Local Services & Offerings" },
      { value: "data-update", label: "Transport or Route Data Update" },
    ],
  },
  {
    emoji: "✨",
    title: "General",
    note: "Anything helpful that doesn’t fit the main groups above.",
    items: [{ value: "others", label: "Others / General Contribution" }],
  },
];

const CategoryModal: React.FC<CategoryModalProps> = ({
  isOpen,
  onClose,
  onSelect,
}) => {
  const handleSelect = (value: string, label: string) => {
    onSelect(value, label);
    onClose();
  };

  return (
    <IonModal
      mode="ios"
      isOpen={isOpen}
      onDidDismiss={onClose}
      className="category-modal"
    >
      <IonHeader>
        <IonToolbar className="toolbar" color="light">
          <IonTitle className="modal-title">Select a category</IonTitle>
          <IonButton
            slot="end"
            fill="clear"
            className="close-button"
            onClick={onClose}
          >
            <IonIcon icon={closeOutline} />
          </IonButton>
        </IonToolbar>
      </IonHeader>

      <IonContent className="modal-content">
        <div className="category-container">
          <div className="category-hero">
            <div className="category-hero-badge">
              <IonIcon icon={sparklesOutline} />
              <span>Contribution type</span>
            </div>
            <p className="category-hero-copy">
              Choose the category that best matches what you want to share so we
              can review it faster and place it in the right part of Bacolod AI.
            </p>
          </div>

          <div className="category-sections">
            {CATEGORY_SECTIONS.map((section) => (
              <section key={section.title} className="category-section-card">
                <div className="category-section-head">
                  <div className="category-section-title-wrap">
                    <span className="category-section-emoji" aria-hidden="true">
                      {section.emoji}
                    </span>
                    <div>
                      <h2 className="section-title">{section.title}</h2>
                      <p className="section-note">{section.note}</p>
                    </div>
                  </div>
                </div>

                <IonList className="category-list">
                  {section.items.map((item) => (
                    <IonItem
                      key={item.value}
                      button
                      detail={false}
                      onClick={() => handleSelect(item.value, item.label)}
                    >
                      <IonLabel>{item.label}</IonLabel>
                    </IonItem>
                  ))}
                </IonList>
              </section>
            ))}
          </div>
        </div>
      </IonContent>
    </IonModal>
  );
};

export default CategoryModal;
